import type { PerfHostTrend, PerfResponse } from "./types";

// The perf panel's headline (OMG-2767), pure so the crash case stays pinned.
//
// A crashed or install-failed night reports to omg-api WITHOUT a payload, so
// it becomes `latest` (a red conclusion, `capturedAt: null`) while every
// card still shows the night before. Counting fails from those cards alone
// said "no site broke a byte budget" on a run that never measured anything
// (Jason, admin#35 review). The headline therefore checks the newest run
// first, and dates the cards by the newest night that did write a summary.

export type PerfHeadlineTone = "ok" | "warn" | "bad";

export interface PerfHeadline {
  tone: PerfHeadlineTone;
  text: string;
  /** When the nightly summary the cards show was captured. */
  summaryAt: Date | null;
}

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** Newest point across every card: the night the cards actually show. */
export const latestSummaryAt = (hosts: PerfHostTrend[]): Date | null => {
  let newest: number | null = null;
  for (const h of hosts) {
    const at = h.points[h.points.length - 1]?.at;
    const t = at ? Date.parse(at) : NaN;
    if (Number.isFinite(t) && (newest === null || t > newest)) newest = t;
  }
  return newest === null ? null : new Date(newest);
};

export const perfHeadline = (data: PerfResponse): PerfHeadline => {
  const summaryAt = latestSummaryAt(data.hosts);
  const latest = data.latest;

  if (latest && latest.conclusion !== "success" && !latest.capturedAt) {
    // Say what actually happened: a cancelled or timed-out night is not a
    // failure (admin#35 review).
    const what =
      latest.conclusion === "cancelled"
        ? "was cancelled"
        : latest.conclusion === "timed_out"
          ? "timed out"
          : latest.conclusion === "skipped"
            ? "was skipped"
            : "failed";
    return {
      tone: "warn",
      text:
        `The last run ${what} before writing a summary, so nothing was measured; ` +
        "the cards show the last night that did.",
      summaryAt,
    };
  }

  const isUnexcused = (severity: string, known: boolean) =>
    !known && severity === "fail";
  const budgetHosts = data.hosts.filter((h) =>
    h.findings.some((f) => isUnexcused(f.severity, f.known)),
  ).length;
  const errorHosts = data.hosts.filter((h) =>
    h.findings.some((f) => f.severity === "error"),
  ).length;
  // The Lighthouse read (OMG-2795): how many sites load slowly, by LCP.
  // `metrics` is optional-chained on purpose: an omg-api that has not shipped
  // the OMG-2795 read sends cards without it, and the page must still draw.
  const measured = data.hosts.filter((h) => h.metrics?.lcp.band != null);
  const poorLcp = measured.filter((h) => h.metrics?.lcp.band === "poor").length;
  const slowLcp = measured.filter(
    (h) => h.metrics?.lcp.band === "needs-improvement",
  ).length;
  const lcpText =
    measured.length === 0
      ? ""
      : poorLcp === 0 && slowLcp === 0
        ? ` All ${measured.length} measured sites load in the good LCP band.`
        : ` ${[
            poorLcp > 0 &&
              `${poorLcp} of ${measured.length} sites load poorly (LCP over 4 s)`,
            slowLcp > 0 && `${slowLcp} need improvement`,
          ]
            .filter(Boolean)
            .join(", ")}.`;

  if (budgetHosts === 0 && errorHosts === 0) {
    return {
      tone: poorLcp > 0 ? "warn" : "ok",
      text: `No site broke a byte budget on the last run.${lcpText}`,
      summaryAt,
    };
  }
  const parts = [
    budgetHosts > 0 &&
      `${plural(budgetHosts, "site", "sites")} broke a byte budget`,
    errorHosts > 0 && `${plural(errorHosts, "site", "sites")} failed to load`,
  ].filter(Boolean);
  return {
    tone: "bad",
    text: `${parts.join("; ")} on the last run.${lcpText}`,
    summaryAt,
  };
};
